import React from 'react';
import { Redirect, useRouteMatch } from 'react-router-dom';
import { useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';

import { AlbumFolderWithPrepaidPrintPackagesInfo } from '../../../graphql/queries/album-folder';
import Spinner from '../../global/Spinner';
import {
  MeWithPurchasedPrintPackages,
  MeWithSelectedIdentities,
} from '../../../graphql/queries/account';
import { PURCHASED_PRINT_PACKAGE_STATUS } from '../../../constants/print-packages';

const AlbumFolderAllowsPrepaidPrintPackages = ({ forceRedirect } = {}) => {
  return WrappedComponent => props => {
    const match = useRouteMatch();
    const { albumFolderId } = match?.params ?? {};
    const authenticated = useSelector(({ auth }) => auth.authenticated);

    const { data: { albumFolder } = {}, loading } = useQuery(
      AlbumFolderWithPrepaidPrintPackagesInfo,
      {
        variables: {
          id: albumFolderId,
        },
        skip: !albumFolderId,
      },
    );
    const {
      data: { me: meWithIdentities } = {},
      loading: loadingAccount,
    } = useQuery(MeWithSelectedIdentities, {
      variables: {
        albumFolderIds: [albumFolderId],
      },
      fetchPolicy: 'network-only',
      skip: !authenticated || !albumFolderId,
    });

    const {
      data: { me: meWithPurchasedPackages } = {},
      loading: loadingPackages,
    } = useQuery(MeWithPurchasedPrintPackages, {
      variables: {
        is_prepaid: true,
        album_folder_id: albumFolderId,
        status: PURCHASED_PRINT_PACKAGE_STATUS.PENDING,
      },
      fetchPolicy: 'network-only',
      skip: !authenticated || !albumFolderId,
    });

    if (loading || loadingAccount || loadingPackages) {
      return (
        <div className="wrapper-main min-h-100vh justify-content-center align-items-center">
          <Spinner />
        </div>
      );
    }

    if (!loading && !albumFolder) {
      return <Redirect to="/not-found" />;
    }

    if (!authenticated) {
      return <WrappedComponent {...props} />;
    }

    // For Allowing Print Packages, we have the following rules:
    // 1. Album Folder must allow Pre-paid (prepaid_print_packages_enabled)
    if (!albumFolder.prepaid_print_packages_enabled) {
      return <WrappedComponent {...props} />;
    }

    // 2. Album Folder must be Picture Day & have Print Packages
    if (
      !albumFolder.picture_day_enabled ||
      albumFolder.album_folder_print_packages.total === 0
    ) {
      return <WrappedComponent {...props} />;
    }

    // if the 2 rules above are covered, we can act based off the props submitted.
    // One of said states that if we have joined identities & no purchases we force
    // a redirect.
    const subbedIdentities =
      meWithIdentities?.selected_identities?.records ?? [];
    const prepaidPackages =
      meWithPurchasedPackages?.purchased_print_packages?.records ?? [];
    if (
      forceRedirect &&
      subbedIdentities.length > 0 &&
      prepaidPackages.length === 0
    ) {
      return (
        <Redirect
          to={`/discover/album-folder/${albumFolderId}/print-packages`}
        />
      );
    }

    return (
      <WrappedComponent
        {...props}
        allowsPrepaidPrintPackages
        prepaidPrintPackagePromoCode={
          albumFolder.active_promo_codes?.records?.[0]
        }
      />
    );
  };
};

export default AlbumFolderAllowsPrepaidPrintPackages;
